import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Button, PageHeader } from '@/shared/components';
import { useContainerQuery } from '@/shared/hooks/useContainerQuery';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { useOnline } from '@/shared/hooks/useOnline';
import { InboxConversation } from './InboxConversation';
import { headerLine, QuietAreaHeader } from './inboxHeader';
import { ThreadList } from './panes/ThreadList';
import { useInboxThreads } from './useInboxThreads';
import { useLayerFocus } from './useLayerFocus';

const CARD = 'bg-card border-hairline md:rounded-tile md:shadow-float overflow-hidden md:border';

/**
 * Nachrichten: Liste, Unterhaltung und Kontext nebeneinander, sobald die Arbeitsfläche dafür breit
 * genug ist (gemessen am Container, nicht am Fenster — die Seitenleiste nimmt 80 oder 268 px).
 * Darunter öffnet der Kontext als Blatt; unter 768 px ist genau eine Ebene sichtbar.
 */
export function InboxPage() {
  const inbox = useInboxThreads();
  const mobile = useMediaQuery('(max-width: 767px)');
  const wide = useContainerQuery(1060);
  const online = useOnline();
  const [contextOpen, setContextOpen] = useState(false);
  const { selectedId, setSelectedId } = inbox;
  const layer = useLayerFocus(mobile, selectedId);
  const showList = !mobile || !selectedId;
  const showConversation = !mobile || !!selectedId;

  const select = (id: string | null) => {
    setContextOpen(false);
    setSelectedId(id);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {mobile && selectedId ? (
        <QuietAreaHeader />
      ) : (
        <PageHeader
          className="pb-[76px] md:pb-[76px]"
          eyebrow="Nachrichten"
          title="Nachrichten"
          description={headerLine(
            inbox.awaitingCount,
            inbox.list.dataUpdatedAt,
            online,
            inbox.list.isError,
          )}
          tools={
            <Button
              type="button"
              variant="ghost-dark"
              className="rounded-pill min-h-11 border border-white/30"
              leftIcon={<RefreshCw size={14} aria-hidden />}
              onClick={inbox.refresh}
              loading={inbox.list.isRefetching}
            >
              Neu laden
            </Button>
          }
          filterTabs={[
            {
              label: 'Nicht beantwortet',
              active: inbox.awaitingOnly,
              onSelect: () => inbox.setAwaitingOnly(true),
              count: inbox.awaitingCount,
            },
            {
              label: 'Alle',
              active: !inbox.awaitingOnly,
              onSelect: () => inbox.setAwaitingOnly(false),
            },
          ]}
          filterTabsLabel="Ansicht"
        />
      )}
      <div
        ref={wide.ref}
        className={`relative z-10 -mx-4 -mb-4 flex min-h-[520px] flex-1 gap-3.5 md:m-0 md:px-2 ${mobile && selectedId ? '' : '-mt-[76px] md:-mt-[76px]'}`}
      >
        {showList && (
          <section
            ref={mobile ? layer : null}
            tabIndex={mobile ? -1 : undefined}
            aria-label="Unterhaltungen"
            className={`${CARD} flex w-full flex-none flex-col outline-none md:w-[340px]`}
          >
            <ThreadList
              threads={inbox.threads}
              selectedId={selectedId}
              onSelect={select}
              filter={inbox.filter}
              onFilter={inbox.setFilter}
              awaitingOnly={inbox.awaitingOnly}
              loading={inbox.listLoading}
              paused={inbox.listPaused}
              error={
                inbox.list.isError && inbox.threads.length === 0 ? inbox.list.error : undefined
              }
              stale={inbox.list.isError && inbox.threads.length > 0}
              onRetry={() => void inbox.list.refetch()}
              hasMore={inbox.list.hasNextPage}
              loadingMore={inbox.list.isFetchingNextPage}
              onLoadMore={() => void inbox.list.fetchNextPage()}
            />
          </section>
        )}
        {showConversation && (
          <InboxConversation
            inbox={inbox}
            className={`${CARD} flex min-w-0 flex-1 flex-col outline-none`}
            layerRef={mobile ? layer : undefined}
            contextAsColumn={wide.matches && !mobile}
            contextOpen={contextOpen}
            onContextOpen={setContextOpen}
            onBack={mobile ? () => select(null) : undefined}
          />
        )}
      </div>
    </div>
  );
}
